import { useEffect, useRef } from "react";
import { useAuth } from "react-oidc-context";
import { resolveBuildId, userDisplayName, useActiveOrg } from "common-fe";
import { HEARTBEAT_INTERVAL_MS } from "score-common";
import { useScoreApi, unwrap } from "../api/scoreApi";
import { refreshCachedStationName } from "../util/stationIdentityCache";
import { useScoreContext } from "../context/ScoreContext";
import { getPresenceLocation } from "../store/presenceLocation";
import { getPresenceInstanceId } from "../util/presenceInstanceId";
import { buildDeviceHealth } from "../util/deviceHealth";
import { getStorageFailure } from "../local/storageFailure";
import {
  getStoragePersistenceInfo,
  subscribeStoragePersistence,
} from "../local/storagePersistence";
import { resolveBuildTime } from "../util/buildTime";
import { INACTIVITY_TIMEOUT_MS } from "./useInactivityRedirect";

// #2364 (scribe-presence workstream, card C) — the FE SENDER half of the
// heartbeat contract score-api's putHeartbeat documents (apps/score-api/src/
// lib/presence.js file header): "the fire-and-forget guarantee lives on the
// SENDER side (#2364, scribe-fe), not here: this module's putHeartbeat DOES
// propagate a DynamoDB failure to its caller". Everything below exists to
// make that promise true from this hook outward:
//
//   - Calls the generated PresenceApi client DIRECTLY (scoreApi.presence,
//     api/scoreApi.js), never through ScoreContext's apiAdapter/syncEngine
//     — a failed heartbeat must never enter the scoring outbox (card's own
//     rule) or be retried by the sync engine's backoff.
//   - Every failure — a synchronous throw building the request, a rejected
//     promise (4xx/5xx/network) — is swallowed right here. Nothing in this
//     hook ever throws into a render or an unhandled rejection, shows a
//     toast/banner, or retries. In particular this NEVER touches
//     syncEngine.setAuthFailureHandler / signinSilent: a 401 on a heartbeat
//     is a silent no-op, so it can never set CognitoAuthProvider's
//     auth.error and trip AuthGate's full-screen unmount (#1289) while a
//     scribe is mid-ride.
//   - The interval itself never blocks/delays anything: setInterval fires
//     independently of React's render/commit cycle and of every scoring
//     write path.
//   - Tab hidden, or idle past INACTIVITY_TIMEOUT_MS (the SAME threshold
//     useInactivityRedirect already uses to route a forgotten kiosk to
//     standby — reused here rather than inventing a second magic number),
//     suspends sending outright: "stale presence is worse than no
//     presence" (card description). The row simply ages past
//     PRESENCE_TTL_SECONDS and disappears from every reader — no explicit
//     "gone" signal is sent or needed.
//     #3649: the interval is the only sender, so on its own a tab that comes
//     back (screen wake, or a scribe tab behind Score Admin) or a tablet
//     touched after a long idle would wait up to a full tick to hear a
//     server-side change (an admin's station rename rides the reply). So the
//     tab becoming visible, and the first touch after idle, each send ONE
//     beat at once. Neither adds a steady cadence: the suspend rule above
//     still governs every tick.
//   - Sends once signed in with a show selected (#3549), seat or no seat.
//     getPresenceLocation (set by ScoringForm — see that store's own header
//     comment for why that is the ONLY screen where ring/classId/rideId/seat
//     are simultaneously known) is null while browsing ClassList/RideList/
//     ClassGrid, so those beats carry ring/classId/rideId/seat as null. The
//     station shows on the Station Monitor from the first beat; score-api opens
//     a work segment only when a seat arrives.
//
// #3436: every beat also carries a `health` object (util/deviceHealth.js) for
// the Station Monitor: storage persistence/usage/quota, storage-full, auth
// expiry, effective connectivity and the sync counts. All of it is read from
// sources the app already keeps (no new measuring), through refs so a change in
// any of them never restarts the interval.
//
// Mounted exactly once (App.jsx, inside ScoreProvider) — NOT per-screen —
// so the interval survives route changes between rides/classes; only the
// location it reads changes.
export function usePresenceHeartbeat() {
  const scoreApi = useScoreApi();
  const auth = useAuth();
  const scoreState = useScoreContext();
  const { selectedShow } = scoreState;
  const showId = selectedShow?.id ?? null;
  const isAuthenticated = Boolean(auth.isAuthenticated);
  const scribeName = userDisplayName(auth.user?.profile) || null;
  // #3520: the heartbeat reply carries the station's current name (an admin may
  // have renamed it); read through a ref so a change never restarts the interval.
  const { activeOrgId } = useActiveOrg();
  const orgIdRef = useRef(activeOrgId);
  orgIdRef.current = activeOrgId;
  const lastActivityRef = useRef(Date.now());
  // Latest ScoreContext values and storage-persistence record, for send().
  const scoreStateRef = useRef(scoreState);
  scoreStateRef.current = scoreState;
  const persistenceRef = useRef(null);
  // #3649: the current effect's send(), so the activity listener below can fire
  // one at once on the first touch after idle. Null while not sending.
  const sendNowRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    getStoragePersistenceInfo()
      .then((cached) => {
        if (!cancelled && cached && !persistenceRef.current) persistenceRef.current = cached;
      })
      .catch(() => {});
    const unsubscribe = subscribeStoragePersistence((next) => {
      if (!cancelled) persistenceRef.current = next;
    });
    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, []);

  // Same lightweight activity signal useInactivityRedirect watches
  // (pointer/keyboard/touch/wheel), tracked independently here so this
  // hook's idle decision never depends on that hook's route-navigation
  // side effect actually having fired yet.
  useEffect(() => {
    const markActive = () => {
      const now = Date.now();
      const wasIdle = now - lastActivityRef.current > INACTIVITY_TIMEOUT_MS;
      lastActivityRef.current = now;
      // #3649: the first touch after idle sends one heartbeat at once, so an
      // admin's rename (or any server-side change in the reply) reaches the
      // tablet on its NEXT touch, not up to a full interval later.
      if (wasIdle) sendNowRef.current?.();
    };
    const events = ["pointerdown", "pointermove", "keydown", "touchstart", "wheel"];
    for (const evt of events) {
      window.addEventListener(evt, markActive, { passive: true });
    }
    return () => {
      for (const evt of events) {
        window.removeEventListener(evt, markActive);
      }
    };
  }, []);

  useEffect(() => {
    if (!isAuthenticated || !showId) return undefined;

    const send = () => {
      if (typeof document !== "undefined" && document.hidden) return;
      if (Date.now() - lastActivityRef.current > INACTIVITY_TIMEOUT_MS) return;
      // #3549: signed in with a show selected is enough. Until a sheet is open
      // there is no ring or seat, so they go as null: the station still shows
      // on the Station Monitor, and the server opens no work segment for it.
      const location = getPresenceLocation();
      const seat = location?.seat || null;
      // The org this heartbeat is SENT under: the reply must not write its name
      // into whichever org is active when it lands (an org switch mid-flight).
      const sentOrgId = orgIdRef.current;
      try {
        scoreApi.presence
          .postHeartbeat({
            showId,
            instanceId: getPresenceInstanceId(),
            ring: seat ? location.ring ?? null : null,
            classId: seat ? location.classId ?? null : null,
            rideId: seat ? location.rideId ?? null : null,
            seat,
            scribeName,
            appBuild: resolveBuildId(),
            health: buildDeviceHealth({
              persistence: persistenceRef.current,
              storageFailure: getStorageFailure(),
              scoreState: scoreStateRef.current,
            }),
            appBuiltAt: resolveBuildTime(),
          })
          .then((res) => {
            try {
              refreshCachedStationName(sentOrgId, unwrap(res)?.stationName);
            } catch {
              // A reply we cannot read is just no new name.
            }
          })
          .catch(() => {
            // Fire-and-forget: a failed heartbeat is silent, full stop.
          });
      } catch {
        // A synchronous throw building/sending the request is just as
        // silent — never propagate.
      }
    };

    // #3649: the tab coming back (a tablet screen wake, or a scribe tab behind
    // Score Admin on the same laptop) sends at once. The interval is suspended
    // while hidden, so without this the new name waits for the next tick. A
    // person bringing the tab forward is activity, so it also clears the idle
    // gate: otherwise a tablet asleep past INACTIVITY_TIMEOUT_MS would wake
    // and still stay quiet until a touch.
    const onVisibility = () => {
      if (typeof document === "undefined" || document.hidden) return;
      lastActivityRef.current = Date.now();
      send();
    };
    document.addEventListener("visibilitychange", onVisibility);
    sendNowRef.current = send;

    const id = setInterval(send, HEARTBEAT_INTERVAL_MS);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVisibility);
      if (sendNowRef.current === send) sendNowRef.current = null;
    };
  }, [scoreApi, showId, isAuthenticated, scribeName]);
}

export default usePresenceHeartbeat;
